import React, { FC, useLayoutEffect, useRef, useState } from 'react';
import classes from 'components/ColumnsBalancer/style/columnsBalancer.module.scss';
import { balancer } from './utils';
import debounce from 'lodash/debounce';
import { ColumnsBalancerItem } from './ColumnsBalancerItem';
import classNames from 'classnames';
import { FlexGrid } from '../FlexGrid';
import { GapSizes } from '../FlexGrid/types';

type ColumnsBalancerProps = {
	maxColumns?: number;
	minMaxColumnWidth?: number;
	columnClassName?: string;
	rowGap?: GapSizes;
};

type ReactChildArray = ReturnType<typeof React.Children.toArray>;

export const ColumnsBalancer: FC<React.PropsWithChildren<ColumnsBalancerProps>> = ({
	children,
	maxColumns = 3,
	minMaxColumnWidth = 320,
	columnClassName,
	rowGap,
}) => {
	const isMountedRef = useRef(true);
	const columnsBalancerRef = useRef<HTMLDivElement>(null);
	const [columnsLength, setColumnsLength] = useState(maxColumns);

	const style: React.CSSProperties = {
		gridTemplateColumns: `repeat(${columnsLength}, 1fr)`,
	};

	useLayoutEffect(() => {
		const resizeObserver = new ResizeObserver(
			debounce(
				(entries) => {
					entries.forEach((entry) => {
						const maxColumnsCount = Math.floor(entry.contentRect.width / minMaxColumnWidth);
						const newColumnsCount = (maxColumnsCount >= maxColumns ? maxColumns : maxColumnsCount) || 1;
						if (isMountedRef.current) {
							setColumnsLength(newColumnsCount);
						}
					});
				},
				100,
				{ leading: true },
			),
		);

		if (columnsBalancerRef.current) {
			resizeObserver.observe(columnsBalancerRef.current);
		}

		return () => {
			isMountedRef.current = false;
			resizeObserver.disconnect();
		};
	}, [maxColumns, minMaxColumnWidth]);
	const childrenIsArray = children instanceof Array;

	return (
		<div
			ref={columnsBalancerRef}
			className={classes.columnsBalancer}
			style={style}
		>
			{childrenIsArray &&
				balancer(
					(children as ReactChildArray).filter((child) => child),
					columnsLength,
				).map((column, idx) => (
					<div
						className={classNames(classes.columnsBalancer_column, columnClassName)}
						key={idx}
					>
						<FlexGrid
							column
							gap={rowGap}
						>
							{column.map((item, key) => (
								<ColumnsBalancerItem key={key}>{item}</ColumnsBalancerItem>
							))}
						</FlexGrid>
					</div>
				))}
			{!childrenIsArray && <div className={classes.columnsBalancer_column}>{children}</div>}
		</div>
	);
};
